import { components } from "@/api-schema";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { DataTableRow } from "@/components/data-table/types";
import { RowAction } from "@/components/ui/table-row-actions";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import type { RelocationOrderExternalActionsContext } from "./_helpers/builders/external/relocation-order-external-actions-context";
import { buildExternalFlowActions } from "./_helpers/builders/external/relocation-order-external-flow-actions";

type RelocationOrder = components["schemas"]["RelocationOrder"];
type RelocationOrderRow = components["schemas"]["RelocationOrder"] & RowWithIdentifier;

interface UseRelocationOrderExternalActionsReturn {
  getActionsForOrder: (order: RelocationOrder) => RowAction[];
  customRowActions: (row: DataTableRow<RelocationOrderRow>) => RowAction[];
}

export function useRelocationOrderExternalActions(): UseRelocationOrderExternalActionsReturn {
  const { t } = useTranslation(["fit", "common"]);
  const [_, navigate] = useLocation();

  const navigateToSetPickupDateView = useCallback(
    (order: RelocationOrder) => {
      navigate(`~/fit/relocation-request/${order.uuid}/set-pickup-date`);
    },
    [navigate],
  );

  const context = useMemo<RelocationOrderExternalActionsContext>(
    () => ({ t, navigateToSetPickupDateView }),
    [t, navigateToSetPickupDateView],
  );

  const getActionsForOrder = useCallback(
    (order: RelocationOrder): RowAction[] => buildExternalFlowActions(order, context),
    [context],
  );

  const customRowActions = useCallback(
    (row: DataTableRow<RelocationOrderRow>) => getActionsForOrder(row.original),
    [getActionsForOrder],
  );

  return { getActionsForOrder, customRowActions };
}
